/**
 * useUserManagement
 *
 * Admin dashboard utilities to classify users, format dates, and control
 * section collapse state. Returns helpers and filtered user lists.
 */
import { useState, useMemo } from 'react';

export const useUserManagement = (userData, enterprises) => {
  // Section collapse state
  const [collapsedSections, setCollapsedSections] = useState({
    newPremiumUsers: false,     // Expanded by default
    trialPremiumUsers: false,   // Expanded by default
    activeUsers: false,         // Expanded by default - important to see current activity
    payingPremiumUsers: true,   // Collapsed by default
    nonRenewingUsers: true,     // Collapsed by default
    enterpriseUsers: true,      // Collapsed by default
    newCustomers: true,         // Collapsed by default
    freeUsers: true,            // Collapsed by default
    formerSubscribers: true,    // Collapsed by default
    formerTrialUsers: true,     // Collapsed by default
    deletedUsers: true,         // Collapsed by default
    orphanedUsers: true,        // Collapsed by default
    basicUsers: true            // Collapsed by default
  });

  // Toggle section collapse
  const toggleSectionCollapse = (section) => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // User classification functions
  const isPremiumUser = (user) => {
    const premiumStatuses = ['active', 'trialing'];
    return premiumStatuses.includes(user.subscriptionStatus?.toLowerCase());
  };

  const isActivelyPaying = (user) => {
    return user.subscriptionStatus?.toLowerCase() === 'active';
  };

  const isCurrentlyInTrial = (user) => {
    return user.subscriptionStatus?.toLowerCase() === 'trialing';
  };

  const isPayingPremiumUser = (user) => {
    return isActivelyPaying(user);
  };

  const isNonRenewingPayingUser = (user) => {
    // Must be actively paying premium user
    const isCurrentlyPaying = isActivelyPaying(user);
    // Must have subscriptionCancelAt set (scheduled for cancellation)
    const hasScheduledCancellation = user.subscriptionCancelAt && new Date(user.subscriptionCancelAt) > new Date();
    
    return isCurrentlyPaying && hasScheduledCancellation;
  };

  const isActiveUser = (user) => {
    if (!user.lastActive) return false;
    
    const now = new Date();
    const lastActiveDate = user.lastActive?.toDate ? user.lastActive.toDate() : new Date(user.lastActive);
    const thirtyMinutesAgo = new Date(now.getTime() - (30 * 60 * 1000)); // 30 minutes in milliseconds
    
    return lastActiveDate >= thirtyMinutesAgo;
  };

  const isTrialPremiumUser = (user) => {
    return isCurrentlyInTrial(user);
  };

  const isFreeUser = (user) => {
    return user.isFreeUser === true;
  };

  const isNewCustomer = (user) => {
    if (!user.createdAt) return false;
    
    // Get today's date in CST timezone
    const now = new Date();
    const cstOffset = -6 * 60; // CST is UTC-6 (in minutes)
    const nowCST = new Date(now.getTime() + (cstOffset + now.getTimezoneOffset()) * 60000);
    
    // Set to start of today in CST (midnight)
    const todayStartCST = new Date(nowCST);
    todayStartCST.setHours(0, 0, 0, 0);
    
    // Convert user creation time to CST
    const userCreatedAt = user.createdAt?.toDate ? user.createdAt.toDate() : new Date(user.createdAt);
    const userCreatedAtCST = new Date(userCreatedAt.getTime() + (cstOffset + userCreatedAt.getTimezoneOffset()) * 60000);
    
    // Check if user was created today in CST
    return userCreatedAtCST >= todayStartCST;
  };

  const isNewPremiumUser = (user) => {
    return (isPayingPremiumUser(user) || isTrialPremiumUser(user)) && isNewCustomer(user);
  };

  const isTrialEndingToday = (user) => {
    const trialEndField = user.trialEnd || user.subscriptionPeriodEnd;
    if (!trialEndField) return false;
    const today = new Date();
    const trialEnd = trialEndField.toDate ? trialEndField.toDate() : new Date(trialEndField);
    today.setHours(0, 0, 0, 0);
    trialEnd.setHours(0, 0, 0, 0);
    return today.getTime() === trialEnd.getTime();
  };

  const isFormerSubscriber = (user) => {
    // MUST NOT be currently premium or in trial
    if (isPremiumUser(user)) return false;
    if (user.planLevel === 'premium') return false;
    if (user.subscriptionStatus === 'active') return false;
    if (user.isInTrial) return false;
    
    // Must not be free or new customer
    if (isFreeUser(user) || isNewCustomer(user)) return false;
    
    // Must have actually been a PAYING customer (not just trial)
    // Check for evidence of actual payments, not just Stripe setup
    const hasActualPaymentHistory = !!(user.firstPaymentDate || user.totalRevenue > 0);
    
    // Must have Stripe history indicating they had a subscription
    const hasStripeHistory = user.stripeCustomerId || user.stripeSubscriptionId || user.subscriptionPeriodStart || user.subscriptionPeriodEnd;
    
    // Must have generated revenue (actual paying customer, not just trial)
    const hadRevenue = (user.totalRevenue && user.totalRevenue > 0) || (user.monthlyPrice && user.monthlyPrice > 0);
    
    // Must be currently basic/cancelled
    const isCurrentlyBasic = !isPremiumUser(user) && user.planLevel !== 'premium' && user.subscriptionStatus !== 'active';
    
    return isCurrentlyBasic && hasStripeHistory && hasActualPaymentHistory && hadRevenue;
  };

  const isFormerTrialUser = (user) => {
    // MUST NOT be currently premium or in trial
    if (isPremiumUser(user)) return false;
    if (user.planLevel === 'premium') return false;
    if (user.subscriptionStatus === 'active') return false;
    if (user.isInTrial) return false;
    
    // Must not be free or new customer
    if (isFreeUser(user) || isNewCustomer(user)) return false;
    
    // Must have Stripe history (had a trial setup)
    const hasStripeHistory = user.stripeCustomerId || user.stripeSubscriptionId || user.hasHadFreeTrial;
    
    // Must NOT have been a paying customer (trial only)
    const neverPaid = !user.firstPaymentDate && (!user.totalRevenue || user.totalRevenue <= 0);
    
    // Must be currently basic/cancelled
    const isCurrentlyBasic = !isPremiumUser(user) && user.planLevel !== 'premium' && user.subscriptionStatus !== 'active';
    
    return isCurrentlyBasic && hasStripeHistory && neverPaid;
  };

  const isBasicUser = (user) => {
    return !isPremiumUser(user) && !isFreeUser(user) && !user.stripeCustomerId;
  };

  const isOrphanedUser = (user) => {
    // Orphaned users are users that exist in the orphanedUsers array from the API
    return false; // This will be handled differently as it comes from userData.orphanedUsers
  };

  const hasCancelledTrial = (user) => {
    const hasStripeHistory = user.stripeCustomerId;
    if (!hasStripeHistory) return false;

    const isScheduledForCancellation = user.subscriptionCancelAt && new Date(user.subscriptionCancelAt) > new Date();

    const cancelledTrialStatuses = [
      'incomplete_expired',
      'cancelled',
      'canceled'
    ];

    const statusIndicatesCancellation = user.detailedSubscriptionStatus &&
      cancelledTrialStatuses.includes(user.detailedSubscriptionStatus.toLowerCase());

    const isBasicWithTrialHistory = !isCurrentlyInTrial(user) &&
      !isPayingPremiumUser(user) &&
      !isFreeUser(user) &&
      hasStripeHistory;

    return isScheduledForCancellation || statusIndicatesCancellation || isBasicWithTrialHistory;
  };

  const getCancellationBadgeInfo = (user) => {
    if (!hasCancelledTrial(user)) return null;

    const isScheduledForCancellation = user.subscriptionCancelAt && new Date(user.subscriptionCancelAt) > new Date();

    if (isScheduledForCancellation) {
      return {
        text: 'Not Renewing',
        className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
      };
    }

    // Determine if this was a paying customer or trial-only user
    const wasPayingCustomer = !!(user.firstPaymentDate || user.totalRevenue > 0);
    
    if (wasPayingCustomer) {
      return {
        text: 'Subscription Cancelled',
        className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
      };
    } else {
      return {
        text: 'Trial Cancelled',
        className: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
      };
    }
  };

  // Get enterprise name for user
  const getEnterpriseNameForUser = (userEmail, userData) => {
    if (!userEmail || !Array.isArray(enterprises) || enterprises.length === 0 || !userData) return null;

    // If user has explicit enterprise flags, try to resolve by id first
    const isEnterpriseFlag = userData.isEnterpriseUser === true || userData.isEnterprise === true || !!userData.enterpriseId;

    // Case-insensitive domain matching helper
    const emailLower = String(userEmail).toLowerCase();

    // Prefer matching by enterpriseId when available
    let matchingEnterprise = null;
    if (userData.enterpriseId) {
      matchingEnterprise = enterprises.find(ent => ent.id === userData.enterpriseId);
    }

    // Fallback: match by domain if not found by id
    if (!matchingEnterprise) {
      matchingEnterprise = enterprises.find(ent => {
        const entDomain = String(ent.enterpriseDomain || '').toLowerCase();
        return entDomain && emailLower.endsWith(`@${entDomain}`);
      });
    }

    // If a matching enterprise is found, return its name. If not, only return null.
    // Do not require isEnterpriseUser to be true if the domain matches.
    if (matchingEnterprise) return matchingEnterprise.enterpriseName || matchingEnterprise.name || null;

    // If no enterprise matched by id or domain, but user explicitly flagged enterprise, return a generic label
    if (isEnterpriseFlag) return 'Enterprise';

    return null;
  };

  // Get enterprise metadata (name, image, id) for a user
  const getEnterpriseMetaForUser = (userEmail, userData) => {
    if (!userEmail || !Array.isArray(enterprises) || enterprises.length === 0 || !userData) return null;

    const emailLower = String(userEmail).toLowerCase();
    let match = null;

    if (userData.enterpriseId) {
      match = enterprises.find(ent => ent.id === userData.enterpriseId);
    }
    if (!match) {
      match = enterprises.find(ent => {
        const entDomain = String(ent.enterpriseDomain || '').toLowerCase();
        return entDomain && emailLower.endsWith(`@${entDomain}`);
      });
    }
    if (!match && (userData.isEnterpriseUser || userData.isEnterprise)) {
      return { enterpriseName: 'Enterprise', enterpriseImage: null, id: null };
    }
    if (!match) return null;
    return {
      enterpriseName: match.enterpriseName || match.name || null,
      enterpriseImage: match.enterpriseImage || match.logoUrl || null,
      id: match.id || null
    };
  };

  // Format dates
  const formatDate = (date) => {
    if (!date) return 'N/A';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'America/Chicago'
    });
  };

  const formatExactDateTime = (date) => {
    if (!date) return 'N/A';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'America/Chicago',
      timeZoneName: 'short'
    });
  };

  const timeAgo = (date) => {
    if (!date) return '';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    const now = new Date();
    const diffInMs = now - dateObj;
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInDays > 0) {
      return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
    } else if (diffInHours > 0) {
      return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
    } else if (diffInMinutes > 0) {
      return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  };

  const getTrialEndDate = (user) => {
    // Try multiple possible field names for trial end date
    const trialEndField = user.trialEnd || user.subscriptionPeriodEnd;
    return trialEndField?.toDate ? trialEndField.toDate() : (trialEndField ? new Date(trialEndField) : null);
  };

  const formatSubscriptionEndDate = (date) => {
    if (!date) return 'N/A';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'America/Chicago'
    });
  };

  // Get subscription color
  const getSubscriptionColor = (status) => {
    const statusColors = {
      'active': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      'trialing': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      'past_due': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      'canceled': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      'cancelled': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      'incomplete': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
      'incomplete_expired': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
      'unpaid': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
    };
    return statusColors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  };

  // Filtered user groups
  const filteredUsers = useMemo(() => {
    return userData?.users || [];
  }, [userData]);

  return {
    // State
    collapsedSections,
    toggleSectionCollapse,
    
    // User classification
    isPremiumUser,
    isActivelyPaying,
    isCurrentlyInTrial,
    isPayingPremiumUser,
    isNonRenewingPayingUser,
    isActiveUser,
    isTrialPremiumUser,
    isFreeUser,
    isNewCustomer,
    isNewPremiumUser,
    isTrialEndingToday,
    isFormerSubscriber,
    isFormerTrialUser,
    isBasicUser,
    isOrphanedUser,
    hasCancelledTrial,
    getCancellationBadgeInfo,
    
    // Utilities
    getEnterpriseNameForUser,
    getEnterpriseMetaForUser,
    formatDate,
    formatExactDateTime,
    timeAgo,
    getTrialEndDate,
    formatSubscriptionEndDate,
    getSubscriptionColor,
    
    // Data
    filteredUsers
  };
};

export default useUserManagement; 